import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { getDocuments, createDraftWithSettings } from '../../service';
import Loader from '../Loader';
import { useAllDocuments } from '../state/hooks';
import { getLastViewedDraftId } from '../../utils/myDraftsPrefs';
import {
  draftRowId,
  draftRoutePath,
  isMultiResponseDraft,
} from '../../utils/draftRows';
import { getUserRole } from '../../userLocalDetails';
import PopupDialogueBox from '../../components2/PopupDialogueBox';
import EmptyDraftsPage from './EmptyDraftsPage';
import IndepentdentUserSidebar from '../IndependentUser/IndepentdentUserSidebar';
import { BulkUploadDialog } from '../BulkUpload/components/BulkUploadDialog';
import { BulkUploadFullPage } from '../BulkUpload/components/BulkUploadFullPage';
import { draftWizardLocation } from '../BulkUpload/utils/feedbackRun';
import styled from 'styled-components';

const MAIN_SIDEBAR_WIDTH = 80;
const DRAFT_SIDEBAR_OFFSET = MAIN_SIDEBAR_WIDTH;
const DRAFT_SIDEBAR_WIDTH = 219;
const DRAFT_SIDEBAR_COLLAPSED_WIDTH = 66;

const EmptyStateContent = styled.div`
  margin-left: ${(props) =>
    `${props.open ? DRAFT_SIDEBAR_WIDTH : DRAFT_SIDEBAR_COLLAPSED_WIDTH}px`};
  transition: margin-left 0.3s ease-in-out;
`;

// /drafts/:assignmentId sends a non-teacher back here (BU-A-02), so landing a
// student on a multi-response row would bounce /docs <-> /drafts forever.
const opensForViewer = (row) =>
  getUserRole() === 'TEACHER' || !isMultiResponseDraft(row);

export default function NewDocPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [openLeftPanel, setOpenLeftPanel] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { resetData } = useAllDocuments();

  useEffect(() => {
    const fetchAndRedirect = async () => {
      try {
        const data = await getDocuments();
        // A row can name nothing openable (an assignment-backed draft whose
        // assignment id is missing), and landing on one used to push
        // /documents/undefined — rows sort newest-first, so a just-created
        // draft is exactly what data[0] hands back.
        const openable = (Array.isArray(data) ? data : []).filter(
          (row) => draftRoutePath(row) && opensForViewer(row)
        );
        if (openable.length > 0) {
          const lastDraftId = getLastViewedDraftId();
          const targetDraft =
            (lastDraftId &&
              openable.find((doc) => String(draftRowId(doc)) === lastDraftId)) ||
            openable[0];
          history.push(draftRoutePath(targetDraft));
        } else {
          setShowEmptyState(true);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching documents:', error);
        setShowEmptyState(true);
        setIsLoading(false);
      }
    };
    fetchAndRedirect();
  }, [history]);

  useEffect(() => {
    if (!showEmptyState) return;
    const params = new URLSearchParams(location.search);
    if (params.get('create') === '1') {
      setShowCreatePopup(true);
      history.replace('/getFeedback');
    }
  }, [showEmptyState, location.search, history]);

  const handleCreateDraft = (settings) =>
    createDraftWithSettings({
      subjectName: settings.subjectName,
      subjectCode: settings.subjectCode,
      taskType: settings.taskType,
      state: settings.state,
      year: settings.year,
      responseType: settings.responseType,
      totalMarks: settings.totalMarks,
      title: 'Untitled Question',
    })
      .then(async (response) => {
        if (response?.id) {
          await Promise.resolve(resetData?.());
          history.push(`/documents/${response.id}`);
        }
      })
      .catch(() => {});

  if (isLoading) return <Loader />;

  if (!showEmptyState) return null;

  return (
    <>
      {showCreatePopup && (
        <PopupDialogueBox
          open={showCreatePopup}
          close={() => setShowCreatePopup(false)}
          mode="create"
          onCreateDraft={handleCreateDraft}
        />
      )}
      <IndepentdentUserSidebar
        open={openLeftPanel}
        subjects={[]}
        setSelectedSubject={() => {}}
        selectedSubject={null}
        onToggleSidebar={() => setOpenLeftPanel((prev) => !prev)}
        sidebarOffset={DRAFT_SIDEBAR_OFFSET}
        onBulkUploadClick={() => setShowBulkUpload(true)}
      />
      <BulkUploadDialog open={showBulkUpload} onClose={() => setShowBulkUpload(false)}>
        <BulkUploadFullPage
          frame="dialog"
          entry="drafts"
          onDraftCreated={(assignmentId) => {
            resetData?.();
            history.push(draftWizardLocation(assignmentId));
          }}
          onExit={() => setShowBulkUpload(false)}
        />
      </BulkUploadDialog>
      <EmptyStateContent open={openLeftPanel}>
        <EmptyDraftsPage onCreate={() => setShowCreatePopup(true)} />
      </EmptyStateContent>
    </>
  );
}
